<!-- Primary interactive button with variants, colors, sizes, and async/loading state. -->

<script lang="ts">
import { useButton } from '@nzyme/vue-forms/components/useButton.js';
import { TransitionFade } from '@nzyme/vue-transitions/TransitionFade.jsx';
import { classProp } from '@nzyme/vue-utils/classProp.js';
import { defineProp } from '@nzyme/vue-utils/defineProp.js';
import { onEventEmitter } from '@nzyme/vue-utils/onEventEmitter.js';
import { computed } from 'vue';
import type { Component } from 'vue';

import Loader from '@sigma/assets/loader.svg?component';
import type { ShortcutWithEvent } from '@sigma/client/utils/Shortcut.js';

import ShortcutComponent from './Shortcut.vue';

export type ButtonColor = 'black' | 'blue' | 'gray' | 'green' | 'red' | 'white';
export type ButtonVariant = 'dashed' | 'link' | 'outline' | 'primary';
export type ButtonSize = 'large' | 'medium' | 'small' | 'xlarge';
export type ButtonAlign = 'center' | 'left' | 'right';

export const ButtonProps = {
  ...useButton.props,
  variant: defineProp<ButtonVariant>(),
  color: defineProp<ButtonColor>(),
  size: defineProp<ButtonSize>(),
  icon: defineProp<Component>(),
  label: defineProp<string>(),
  /** Sets both `title` and `aria-label` HTML attributes for accessible icon-only buttons. */
  title: defineProp<string>(),
  contentClass: classProp,
  noMargin: Boolean,
  noContentPadding: Boolean,
  rounded: Boolean,
  uppercase: Boolean,
  iconRight: defineProp<Component>(),
  shortcut: defineProp<ShortcutWithEvent>(),
  align: defineProp<ButtonAlign>(),
};

export const ButtonEmits = useButton.emits;
</script>

<script lang="ts" setup>
import css from './Button.vue?vue&type=style&index=0&lang.module.scss';

const props = defineProps(ButtonProps);
defineEmits(ButtonEmits);

const { Button, busy, disabled, click } = useButton();

const classes = computed(() => {
  const buttonClasses = [css.button];

  if (props.variant) {
    buttonClasses.push(css.variant, css[`variant-${props.variant}`]);
  }

  if (props.color) {
    buttonClasses.push(css[`color-${props.color}`]);
  }

  const size: ButtonSize | undefined = props.size ?? (props.variant ? 'medium' : undefined);
  if (size) {
    buttonClasses.push(css[`size-${size}`]);
  }

  if (props.uppercase) {
    buttonClasses.push(css.uppercase);
  }

  if (props.rounded) {
    buttonClasses.push(css.rounded);
  }

  if (busy.value) {
    buttonClasses.push(css.busy);
  }

  if (disabled.value) {
    buttonClasses.push(css.disabled);
  }

  if (!props.noMargin) {
    buttonClasses.push(css.margin);
  }

  if (props.noContentPadding) {
    buttonClasses.push(css.noContentPadding);
  }

  return buttonClasses;
});

const contentClasses = computed(() => {
  const contentClassList = [css.content];

  if (props.align === 'left') {
    contentClassList.push(css['content-align-left']);
  } else if (props.align === 'right') {
    contentClassList.push(css['content-align-right']);
  }

  if (props.contentClass) {
    return [contentClassList, props.contentClass];
  }

  return contentClassList;
});

const shortcutColor = computed(() => {
  if (props.variant === 'primary' && !props.disabled) {
    return props.color ?? 'blue';
  }

  return null;
});

onEventEmitter(() => props.shortcut?.event, click);
</script>

<template>
  <Button
    :class="classes"
    :aria-busy="busy || undefined"
    :aria-label="title || undefined"
    :title="title || undefined"
  >
    <div :class="css.inner">
      <div :class="contentClasses">
        <component
          :is="icon"
          :class="css.icon"
        />

        <span
          v-if="label"
          :class="css.label"
        >
          {{ label }}
        </span>
        <slot />
        <component
          :is="iconRight"
          :class="css.icon"
        />
      </div>

      <ShortcutComponent
        v-if="shortcut"
        :class="rounded && css.rounded"
        :color="shortcutColor"
        :shortcut="shortcut"
        size="small"
      />
    </div>

    <TransitionFade :duration="100">
      <div
        v-if="busy"
        :class="css.spinner"
      >
        <Loader />
      </div>
    </TransitionFade>
  </Button>
</template>

<style lang="scss" module="css">
.button {
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  background: var(--button-background);
  color: var(--button-color);
  min-width: 0;

  // Default color scheme (blue)
  --button-accent: #{$color-doctor-700};
  --button-accent-hover: #{$color-doctor-500};
  --button-accent-dim: #{$color-doctor-100};
  // Outline/dashed border: a lighter tint of the accent, so the border reads
  // softer than the label color.
  --button-accent-border: #{$color-doctor-200};

  // Default disabled colors
  --button-disabled-background: #{$color-gray-100};
  --button-disabled-color: #{$color-gray-600};
  --button-disabled-border: #{$color-gray-300};
  --button-disabled-outline-color: #{$color-gray-600};
  --button-disabled-outline-border: #{$color-gray-500};
}

// Color variants - define color schemes first
.color {
  &-red {
    --button-accent: #{$color-red-500};
    --button-accent-hover: #{$color-red-500};
    --button-accent-dim: #{$color-red-50};
    --button-accent-border: #{$color-red-100};
  }

  &-green {
    --button-accent: #{$color-green-500};
    --button-accent-hover: #{$color-green-400};
    --button-accent-dim: #{$color-green-200};
    --button-accent-border: #{$color-green-200};
  }

  &-gray {
    --button-accent: #{$color-gray-600};
    --button-accent-hover: #{$color-gray-700};
    --button-accent-dim: #{$color-gray-50};
    --button-accent-border: #{$color-gray-100};
  }

  &-black {
    --button-accent: #{$color-gray-900};
    --button-accent-hover: #{$color-gray-800};
    --button-accent-dim: #{$color-doctor-100};
    --button-accent-border: #{$color-gray-200};
  }

  &-white {
    --button-accent: #{$color-white};
    --button-accent-hover: #{$color-gray-100};
    --button-accent-dim: rgba(255, 255, 255, 0.15);
    --button-accent-border: #{$opacity-white-30};
  }
}

// Base variant styles
.variant {
  font-weight: $font-weight-400;
  text-decoration: none;
  position: relative;
  border: 1px solid var(--button-border-color);
  border-radius: 0.65em;
  transition:
    transform 0.1s ease,
    color 0.2s ease-out,
    background-color 0.2s ease-out,
    border-color 0.2s ease-out,
    box-shadow 0.2s ease-out;
  --button-padding-content: var(--button-padding, 0.5em);
  padding: var(--button-padding, 0.5em);
  letter-spacing: 0;
  contain: content;

  &:focus-visible {
    outline: 2px var(--button-accent);
    outline-offset: 2px;
  }

  &:active {
    transform: scale(0.95);
  }

  // Primary variant
  &-primary {
    --button-background: var(--button-accent);
    --button-color: #{$color-white};
    --button-border-color: var(--button-accent);
    box-shadow: 0 0 0 0 var(--button-accent);

    @media (hover: hover) {
      &:hover:not(.disabled) {
        box-shadow: 0 0 0 2px var(--button-accent);
      }
    }

    &.disabled {
      --button-background: var(--button-disabled-background);
      --button-color: var(--button-disabled-color);
      --button-border-color: var(--button-disabled-background);
      box-shadow: none;
    }

    &.color-white:not(.disabled) {
      --button-color: #{$color-doctor-600};
    }
  }

  // Outline variant
  &-outline {
    --button-background: transparent;
    --button-color: var(--button-accent);
    --button-border-color: var(--button-accent-border);

    @media (hover: hover) {
      &:hover:not(.disabled) {
        --button-background: var(--button-accent-dim);
        --button-border-color: var(--button-accent);
      }
    }

    &.disabled {
      --button-background: transparent;
      --button-color: var(--button-disabled-outline-color);
      --button-border-color: var(--button-disabled-outline-border);
    }
  }

  // Link variant
  &-link {
    --button-background: transparent;
    --button-color: var(--button-accent);
    --button-border-color: transparent;

    @media (hover: hover) {
      &:hover:not(.disabled) {
        --button-background: var(--button-accent-dim);
      }
    }

    &.disabled {
      --button-background: transparent;
      --button-color: var(--button-disabled-outline-color);
      --button-border-color: transparent;
    }
  }

  // Dashed variant
  &-dashed {
    --button-background: transparent;
    --button-color: var(--button-accent);
    --button-border-color: var(--button-accent);
    border-style: dashed;
    border-width: 1px;

    @media (hover: hover) {
      &:hover:not(.disabled) {
        --button-background: var(--button-accent-dim);
      }
    }

    &.disabled {
      --button-background: transparent;
      --button-color: var(--button-disabled-outline-color);
      --button-border-color: var(--button-disabled-outline-border);
    }
  }
}

// Size variants
.size {
  &-small {
    font-size: var(--button-font-size, $font-size-12);
  }

  &-medium {
    font-size: var(--button-font-size, $font-size-14);
  }

  &-large {
    font-size: var(--button-font-size, $font-size-16);
  }

  &-xlarge {
    font-size: var(--button-font-size, $font-size-18);
    --button-padding: 0.6em;
  }
}

// Modifier classes
.margin {
  // makes the button slightly bigger for clicking on touch devices
  &:before {
    content: '';
    display: block;
    position: absolute;
    top: -$spacing-08;
    bottom: -$spacing-08;
    left: -$spacing-08;
    right: -$spacing-08;
  }

  &:active:before {
    transform: scale(1.06);
  }
}

.noContentPadding {
  --button-padding-content: 0;
}

.uppercase {
  text-transform: uppercase;
  letter-spacing: $letter-spacing-05;
}

.rounded {
  border-radius: 999px !important;
}

.align {
  &-left {
    justify-content: flex-start;
    text-align: left;
  }

  &-right {
    justify-content: flex-end;
    text-align: right;
  }
}

.busy {
  cursor: wait;
}

.disabled {
  cursor: not-allowed;

  &:hover {
    transform: none !important;
  }

  &:active {
    transform: none !important;
  }
}

.inner {
  display: flex;
  transition: opacity 0.1s ease-out;
  align-items: center;
  justify-content: space-between;

  .busy & {
    opacity: 0;
  }
}

.content {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  max-width: 100%;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  // Roughly a width of a single space
  gap: 0.5ch;
  padding-inline: var(--button-padding-content);
  margin-inline: auto;

  &-align-left {
    margin-left: 0;
  }

  &-align-right {
    margin-right: 0;
  }
}

.label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spinner {
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  padding: inherit;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    height: 100%;
    max-height: 24px;
    stroke-width: 2px;
  }
}

.icon {
  width: 1.2em;
  height: 1.2em;
}
</style>
